import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldAlert, Loader2, AlertTriangle } from "lucide-react";

const STATUS_FILTERS = [
  { value: "open", label: "Open" },
  { value: "actioned", label: "Actioned" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

const ACTION_OPTIONS = [
  { value: "dismiss", label: "Dismiss (lift auto-hide)", className: "bg-slate-600 hover:bg-slate-700" },
  { value: "warn", label: "Warn the user", className: "bg-amber-600 hover:bg-amber-700" },
  { value: "suspend", label: "Suspend the account", className: "bg-rose-600 hover:bg-rose-700" },
];

const CATEGORY_LABEL = {
  harassment: "Harassment", spam: "Spam", fake_profile: "Fake Profile",
  inappropriate: "Inappropriate", sexual_content: "Sexual Content",
  self_harm: "Self-Harm", violence: "Violence", csam: "CSAM", other: "Other",
};

export default function AdminReportsQueue() {
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reports?status=${status}`);
      setReports(r.data.reports || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      await api.post(`/admin/reports/${activeReport.id}/action`, { action, note });
      toast.success("Action recorded");
      setActiveReport(null);
      setAction("");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-reports-queue">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Moderation
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Reports Queue</h1>
          <p className="text-slate-500 text-sm mt-1">Severe reports auto-hide; review within 24 hours.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]" data-testid="reports-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center text-slate-500 border-slate-200" data-testid="reports-empty">
          🎉 No reports here. The community is healthy.
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <Card key={r.id} className="p-5 border-slate-200" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={r.severe ? "bg-rose-100 text-rose-800 border border-rose-300" : "bg-slate-100 text-slate-700 border border-slate-300"}>
                      {r.severe && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {CATEGORY_LABEL[r.category] || r.category}
                    </Badge>
                    <Badge variant="outline">{r.status}</Badge>
                    <span className="text-[11px] text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-900">
                    <strong>Target:</strong> {r.target?.display_handle || "—"}
                    <span className="text-slate-500 ml-2">({r.target?.email})</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    <strong>Reporter:</strong> {r.reporter?.display_handle || r.reporter?.full_name || "—"}
                  </div>
                  {r.note && (
                    <div className="text-sm text-slate-700 bg-slate-50 p-2 rounded border border-slate-200">
                      "{r.note}"
                    </div>
                  )}
                  {r.action && (
                    <div className="text-xs text-emerald-700">
                      ✓ Actioned: <strong>{r.action}</strong> on {r.reviewed_at?.split("T")[0]}
                      {r.review_note && ` — "${r.review_note}"`}
                    </div>
                  )}
                </div>
                {r.status === "open" && (
                  <Button
                    size="sm"
                    onClick={() => { setActiveReport(r); setAction(""); setNote(""); }}
                    className="bg-[#1E3A8A] hover:bg-[#1E40AF]"
                    data-testid={`review-${r.id}`}
                  >
                    Review
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!activeReport} onOpenChange={(o) => !o && setActiveReport(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Action report</DialogTitle>
            <DialogDescription>
              Pick what to do with <strong>{activeReport?.target?.display_handle}</strong> for {" "}
              <strong>{CATEGORY_LABEL[activeReport?.category]}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="report-action-select"><SelectValue placeholder="Pick action" /></SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`action-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Reviewer notes (optional, max 1000 chars)"
              value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3}
              data-testid="report-action-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveReport(null)}>Cancel</Button>
            <Button onClick={submit} disabled={!action || busy} data-testid="report-action-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
